import { Button, Input, Label, Popover, PopoverAnchor, PopoverContent } from "@tellescope/ui";
import { Editor } from "@tiptap/react";
import { Link2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { MarkButton } from "./MarkButton";
import { useLinkButton } from "./useLinkButton";

export interface LinkButtonProps {
  editor: Editor | null;
}

/** Toolbar control that adds or edits a hyperlink over the current selection via a small popover. */
export const LinkButton = ({ editor }: LinkButtonProps) => {
  const { t } = useTranslation("templates");
  const { open, url, setUrl, text, setText, isActive, handleOpenChange, applyLink, removeLink } = useLinkButton(editor);

  if (!editor) return null;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <MarkButton label={t("Link")} icon={<Link2 />} isActive={isActive} onClick={() => handleOpenChange(!open)} />
      </PopoverAnchor>
      <PopoverContent align="start" className="w-72">
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            // The editor lives inside the template form dialog; stop propagation so submitting the
            // link form doesn't bubble up and submit/close that outer form.
            event.preventDefault();
            event.stopPropagation();
            applyLink();
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="template-link-url">{t("Link URL")}</Label>
            <Input
              id="template-link-url"
              autoFocus
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder={t("https://example.com")}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="template-link-text">{t("Display text")}</Label>
            <Input
              id="template-link-text"
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder={t("Link text")}
            />
          </div>
          <div className="flex justify-end gap-2">
            {isActive && (
              <Button type="button" size="sm" variant="ghost" onClick={removeLink}>
                {t("Remove")}
              </Button>
            )}
            <Button type="submit" size="sm">
              {t("Apply")}
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
};
